import { useTranslation } from 'react-i18next';
import { Badge, Input, Label, RichTextEditor, Select, Switch } from '@knack/asterisk-react';

import { type TableView } from '@/types/schema/views/TableView';
import { BuilderAccordion } from '@/components/BuilderAccordion';
import { usePageEditorContext } from '@/pages/pages/page-editor/PageEditorContext';
import { useActiveViewContext } from '@/pages/pages/settings-panel/view-settings/ActiveViewContextProvider';

const defaultPaginationRowsPerPageNumber = '25';
const paginationRowsPerPageOptions = ['10', defaultPaginationRowsPerPageNumber, '50', '100'];

interface TableSwitchOptionProps {
  label: string;
  disabled?: boolean;
  checked: boolean;
  isComingSoon?: boolean;
  onCheckedChange?: (value: boolean) => void;
}

function TableSwitchOption({
  label,
  disabled,
  checked,
  isComingSoon,
  onCheckedChange
}: TableSwitchOptionProps) {
  const [t] = useTranslation();

  return (
    <Label className="flex items-center gap-2">
      <Switch checked={checked} disabled={disabled} onCheckedChange={onCheckedChange} />
      {label}
      {isComingSoon && (
        <Badge className="px-2 py-0.5 text-xs text-default">{t('keywords.coming_soon')}</Badge>
      )}
    </Label>
  );
}

export function TableGeneralSettingsCategory() {
  const [t] = useTranslation();
  const { updatePage } = usePageEditorContext();
  const { view } = useActiveViewContext<TableView>();

  function updateViewSchema(viewSchema: Partial<TableView>) {
    updatePage({
      type: 'view',
      origin: 'builder',
      action: 'update',
      viewKey: view.key,
      viewSchema
    });
  }

  return (
    <BuilderAccordion>
      <BuilderAccordion.Item
        isDefaultOpen
        label={t('pages.element_settings.general.title_and_description')}
      >
        <div className="mb-4">
          <span className="mb-2 block text-sm font-medium">{t('keywords.title')}</span>
          <Input
            value={view.title ?? ''}
            className="text-sm"
            onChange={(e) => {
              updateViewSchema({
                title: e.target.value
              });
            }}
          />
          <p className="mt-2 text-xs text-subtle">
            {t(
              'pages.element_settings.table.categories.general_settings.leave_this_field_blank_title'
            )}
          </p>
        </div>
        <div>
          <span className="mb-2 block text-sm font-medium">{t('keywords.description')}</span>
          <RichTextEditor
            onUpdate={({ editor }) => {
              updateViewSchema({
                description: editor.getText() ? editor.getHTML() : ''
              });
            }}
            content={view.description || ''}
          />
          <p className="mt-2 text-xs text-subtle">
            {t(
              'pages.element_settings.table.categories.general_settings.leave_this_field_blank_description'
            )}
          </p>
        </div>
      </BuilderAccordion.Item>
      <BuilderAccordion.Item
        isDefaultOpen
        label={t('pages.element_settings.table.categories.general_settings.user_options')}
      >
        <div className="mb-4 space-y-2">
          <TableSwitchOption
            label={t('pages.element_settings.table.categories.general_settings.edit_record_inline')}
            checked={view.options.cell_editor || false}
            onCheckedChange={(value) => {
              updateViewSchema({
                options: {
                  cell_editor: value
                }
              });
            }}
          />
          <TableSwitchOption
            label={t('pages.element_settings.table.categories.general_settings.keyword_search')}
            onCheckedChange={(value) => {
              updateViewSchema({
                keyword_search: value
              });
            }}
            checked={view.keyword_search}
          />
          <TableSwitchOption
            label={t('pages.element_settings.table.categories.general_settings.record_exporting')}
            onCheckedChange={(value) => {
              updateViewSchema({
                allow_exporting: value
              });
            }}
            checked={view.allow_exporting}
          />
          <TableSwitchOption
            label={t('pages.element_settings.table.categories.general_settings.add_record_form')}
            disabled
            isComingSoon
            checked={false}
          />
        </div>
      </BuilderAccordion.Item>
      <BuilderAccordion.Item
        isDefaultOpen
        label={t('pages.element_settings.table.categories.general_settings.pagination')}
      >
        <div className="space-y-2">
          <Label>
            <span className="font-medium">
              {t('pages.element_settings.table.categories.general_settings.default_pagination')}
            </span>
            <Select
              value={view.rows_per_page || defaultPaginationRowsPerPageNumber}
              onValueChange={(value) => {
                updateViewSchema({
                  rows_per_page: value
                });
              }}
            >
              <Select.Trigger className="mt-1 w-full text-sm text-subtle" />
              <Select.Content>
                {paginationRowsPerPageOptions.map((option) => (
                  <Select.Item key={option} className="text-sm" value={option}>
                    {t(
                      'pages.element_settings.table.categories.general_settings.display_record_count',
                      { rowsPerPage: option }
                    )}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
          </Label>
          <TableSwitchOption
            label={t('pages.element_settings.table.categories.general_settings.allow_pagination')}
            onCheckedChange={(value) =>
              updateViewSchema({
                allow_limit: value
              })
            }
            checked={view.allow_limit}
          />
        </div>
      </BuilderAccordion.Item>
    </BuilderAccordion>
  );
}
